/**
 * The eight artifacts of the "Pitch a startup" demo: Offseason's seed round.
 *
 * Offseason books second homes in the months their owners never use them. The deck is the one a founder
 * would put in front of angels for twenty minutes: paper ground, one terracotta accent, a serif headline
 * in the same place on every page, and a real picture doing the talking on each one (a year of nights,
 * the app, the curve, the market, the receipt, the owners, the ask).
 *
 * Drawn with the shared kit in `slideSvg.ts`. Every id made here starts with `pitch-`, because the
 * preview puts the slide inline in the page.
 */

import { esc, line, lines, PHOTO, rect, SANS, SERIF, wrap, type DemoSlide } from './slideSvg';

/** The designer's palette, and the few neutrals the pages need around it. */
const C = {
  paper: '#F4F1EA',
  ink: '#1D1F1C',
  forest: '#2F4A3A',
  sage: '#B9C8A7',
  sand: '#E7DCCB',
  terracotta: '#D9785F',
  mist: '#C9D6E3',
  white: '#FFFFFF',
  /** Running line, axis labels, captions. */
  muted: '#7C786F',
  /** Secondary words next to ink. */
  soft: '#55534D',
  /** Gridlines and rules on paper. */
  hair: '#DDD7CB',
  /** Rules inside white surfaces. */
  rule: '#ECE8E0',
  /** An empty night on the calendar. */
  empty: '#B9B3A7',
  /** The unfilled part of a house or star on white. */
  unfilled: '#D8D1C4',
  /** Terracotta laid thin over white: the receipt row the page is about. */
  highlight: '#F8E5DE',
} as const;

/** Side margins, and where every content page's headline sits. */
const L = 64;
const R = 1216;
const HEAD_Y = 150;
const HEAD_LEAD = 68;
/** Where a picture beside the headline starts: level with the headline's capitals. */
const VISUAL_TOP = 104;
/** Where a picture under a two line headline starts. */
const BELOW_TOP = 300;
/** From the headline's last baseline to the supporting line's. */
const SUPPORT_GAP = 52;

let seq = 0;
const uid = (name: string) => `pitch-${name}-${++seq}`;
const n = (v: number) => String(Math.round(v * 100) / 100);
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

interface TextOpts {
  fill?: string;
  weight?: number;
  anchor?: 'start' | 'middle' | 'end';
  font?: string;
  tracking?: string;
}

/** One line of UI or label type: Inter, no tracking, ink unless told otherwise. */
const t = (x: number, y: number, size: number, str: string, o: TextOpts = {}) =>
  lines(x, y, size, size, [str], { font: SANS, tracking: '0', fill: C.ink, ...o });

/** One line in several voices, e.g. a price in ink and its unit in grey. */
function rich(x: number, y: number, size: number, parts: [string, TextOpts?][], o: TextOpts = {}): string {
  const { anchor = 'start', font = SANS, tracking = '0' } = o;
  const spans = parts
    .map(([s, p = {}]) => `<tspan${p.fill ? ` fill="${p.fill}"` : ''}${p.weight ? ` font-weight="${p.weight}"` : ''}>${esc(s)}</tspan>`)
    .join('');
  return `<text x="${n(x)}" y="${n(y)}" font-family="${font}" font-size="${size}" font-weight="${o.weight ?? 400}" fill="${o.fill ?? C.ink}" text-anchor="${anchor}" letter-spacing="${tracking}">${spans}</text>`;
}

const dot = (cx: number, cy: number, r: number, fill: string) => `<circle cx="${n(cx)}" cy="${n(cy)}" r="${n(r)}" fill="${fill}"/>`;

const ring = (cx: number, cy: number, r: number, stroke: string, width = 1.5) =>
  `<circle cx="${n(cx)}" cy="${n(cy)}" r="${n(r)}" fill="none" stroke="${stroke}" stroke-width="${width}"/>`;

const stroke = (d: string, color: string, width = 1.6) =>
  `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round"/>`;

/* ── photographs ──────────────────────────────────────────────────────────────────────────── */

/** Each photograph's own size, so a crop can hold on what matters instead of the middle. */
const SRC = {
  cliff: [1198, 575],
  arch: [527, 248],
  desert: [527, 561],
  interior: [692, 637],
  stairs: [229, 248],
} as const;

interface Crop {
  r?: number;
  /** The point to keep in view, as fractions of the source. */
  fx?: number;
  fy?: number;
  /** Crops tighter than cover, to lose something at an edge. */
  zoom?: number;
}

/** A photograph covering its box, cropped around a focus point. */
function pic(name: keyof typeof SRC, x: number, y: number, w: number, h: number, { r = 0, fx = 0.5, fy = 0.5, zoom = 1 }: Crop = {}): string {
  const [sw, sh] = SRC[name];
  const s = Math.max(w / sw, h / sh) * zoom;
  const iw = sw * s;
  const ih = sh * s;
  const ix = clamp(x + w / 2 - fx * iw, x + w - iw, x);
  const iy = clamp(y + h / 2 - fy * ih, y + h - ih, y);
  const id = uid('photo');
  return (
    `<clipPath id="${id}"><rect x="${n(x)}" y="${n(y)}" width="${n(w)}" height="${n(h)}" rx="${r}"/></clipPath>` +
    `<image href="${PHOTO[name]}" x="${n(ix)}" y="${n(iy)}" width="${n(iw)}" height="${n(ih)}" preserveAspectRatio="xMidYMid slice" clip-path="url(#${id})"/>`
  );
}

/* ── icons, drawn so they never depend on a glyph ────────────────────────────────────────── */

/** A five point star centred on (cx, cy). */
function starD(cx: number, cy: number, r: number): string {
  const pts = Array.from({ length: 10 }, (_, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 === 0 ? r : r * 0.48;
    return `${n(cx + rr * Math.cos(a))} ${n(cy + rr * Math.sin(a))}`;
  });
  return `M ${pts.join(' L ')} Z`;
}
const star = (cx: number, cy: number, r: number, fill: string) =>
  `<path d="${starD(cx, cy, r)}" fill="${fill}" stroke="${fill}" stroke-width="${n(r * 0.14)}" stroke-linejoin="round"/>`;

/** A heart centred on (cx, cy), `size` wide: solid, or drawn in `outline` when one is given. */
function heart(cx: number, cy: number, size: number, fill: string, outline?: string): string {
  const s = size / 20;
  const d =
    'M12 20.6 L10.6 19.3 C5.4 14.6 2 11.6 2 7.9 C2 4.9 4.4 2.5 7.4 2.5 C9.1 2.5 10.8 3.3 12 4.6 C13.2 3.3 14.9 2.5 16.6 2.5 C19.6 2.5 22 4.9 22 7.9 C22 11.6 18.6 14.6 13.4 19.3 Z';
  const paint = outline ? `fill="none" stroke="${outline}" stroke-width="${n(1.7 / s)}" stroke-linejoin="round"` : `fill="${fill}"`;
  return `<path transform="translate(${n(cx - 12 * s)} ${n(cy - 11.5 * s)}) scale(${n(s)})" d="${d}" ${paint}/>`;
}

const magnifier = (cx: number, cy: number, color: string, r = 5.5) =>
  ring(cx - 1.5, cy - 1.5, r, color, 1.7) + stroke(`M ${n(cx + r * 0.55)} ${n(cy + r * 0.55)} L ${n(cx + r * 1.2)} ${n(cy + r * 1.2)}`, color, 1.7);

/** The small line icons of the listing page, each in a 16 box whose top left is (x, y). */
const ICON = {
  calendar: (x: number, y: number, c: string) =>
    `<rect x="${x + 1}" y="${y + 2.5}" width="14" height="12.5" rx="2.5" fill="none" stroke="${c}" stroke-width="1.5"/>` +
    stroke(`M ${x + 1} ${y + 6.5} L ${x + 15} ${y + 6.5} M ${x + 5} ${y + 0.8} L ${x + 5} ${y + 3.8} M ${x + 11} ${y + 0.8} L ${x + 11} ${y + 3.8}`, c, 1.5),
  house: (x: number, y: number, c: string) =>
    stroke(`M ${x + 1.5} ${y + 7.5} L ${x + 8} ${y + 1.8} L ${x + 14.5} ${y + 7.5} M ${x + 3.5} ${y + 6} L ${x + 3.5} ${y + 14.5} L ${x + 12.5} ${y + 14.5} L ${x + 12.5} ${y + 6}`, c, 1.5),
  bed: (x: number, y: number, c: string) =>
    stroke(
      `M ${x + 1.3} ${y + 2.5} L ${x + 1.3} ${y + 14.5} M ${x + 1.3} ${y + 6} L ${x + 13.3} ${y + 6} C ${x + 14.1} ${y + 6} ${x + 14.7} ${y + 6.6} ${x + 14.7} ${y + 7.4} L ${x + 14.7} ${y + 14.5} M ${x + 1.3} ${y + 11.5} L ${x + 14.7} ${y + 11.5} M ${x + 5} ${y + 6} L ${x + 5} ${y + 11.5}`,
      c,
      1.5,
    ),
  person: (x: number, y: number, c: string) =>
    ring(x + 8, y + 5, 3.4, c, 1.5) + stroke(`M ${x + 1.8} ${y + 15} C ${x + 2.6} ${y + 11} ${x + 5} ${y + 9.6} ${x + 8} ${y + 9.6} C ${x + 11} ${y + 9.6} ${x + 13.4} ${y + 11} ${x + 14.2} ${y + 15}`, c, 1.5),
  bag: (x: number, y: number, c: string) =>
    `<rect x="${x + 1.5}" y="${y + 4.5}" width="13" height="10.5" rx="2.5" fill="none" stroke="${c}" stroke-width="1.5"/>` +
    stroke(`M ${x + 5.5} ${y + 4.5} L ${x + 5.5} ${y + 2.5} C ${x + 5.5} ${y + 1.6} ${x + 6.2} ${y + 1} ${x + 7} ${y + 1} L ${x + 9} ${y + 1} C ${x + 9.8} ${y + 1} ${x + 10.5} ${y + 1.6} ${x + 10.5} ${y + 2.5} L ${x + 10.5} ${y + 4.5}`, c, 1.5),
};

/* ── the phone ────────────────────────────────────────────────────────────────────────────── */

type Screen = (sx: number, sy: number, sw: number, sh: number) => string;

const BEZEL = 6;
const SCREEN_R = 34;

/** A phone: a thin ink bezel, the screen clipped to its rounded corners, and the island on top. */
function phone(x: number, y: number, w: number, h: number, screen: Screen): string {
  const sx = x + BEZEL;
  const sy = y + BEZEL;
  const sw = w - BEZEL * 2;
  const sh = h - BEZEL * 2;
  const id = uid('screen');
  return (
    rect(x, y, w, h, C.ink, SCREEN_R + BEZEL) +
    `<clipPath id="${id}"><rect x="${sx}" y="${sy}" width="${sw}" height="${sh}" rx="${SCREEN_R}"/></clipPath>` +
    `<g clip-path="url(#${id})">${rect(sx, sy, sw, sh, C.white)}${screen(sx, sy, sw, sh)}</g>` +
    rect(x + w / 2 - 36, sy + 10, 72, 22, C.ink, 11)
  );
}

/** Time, signal and battery, in the colour the screen under them needs. */
function statusBar(sx: number, sy: number, sw: number, c: string): string {
  const bx = sx + sw - 44;
  const bars = [4, 6, 8, 10].map((h, i) => rect(bx - 26 + i * 5, sy + 30 - h, 3, h, c, 1)).join('');
  return (
    t(sx + 32, sy + 30.5, 13, '9:41', { fill: c, weight: 500 }) +
    bars +
    `<rect x="${bx}" y="${sy + 20.5}" width="22" height="11" rx="3" fill="none" stroke="${c}" stroke-width="1.2" opacity="0.5"/>` +
    rect(bx + 2, sy + 22.5, 15, 7, c, 1.5) +
    rect(bx + 23.5, sy + 24, 1.6, 4, c, 0.8)
  );
}

/** Search results: the pill, the first home as a big card, the next one as a row, the tab bar. */
const resultsScreen: Screen = (sx, sy, sw, sh) => {
  const x = sx + 12;
  const w = sw - 24;
  const photoY = sy + 128;
  const photoH = 150;
  const tabY = sy + sh - 62;
  const tab = (i: number) => sx + (sw * (2 * i + 1)) / 8 - 8;
  return (
    statusBar(sx, sy, sw, C.ink) +
    // the search pill
    `<rect x="${x + 0.5}" y="${sy + 50.5}" width="${w - 1}" height="43" rx="21.5" fill="${C.white}" stroke="${C.hair}"/>` +
    magnifier(x + 21, sy + 72, C.ink, 5) +
    t(x + 36, sy + 76.5, 13, 'Within 3 hours · 14 to 18 Oct', { weight: 500 }) +
    t(x, sy + 118, 13, '38 homes free those dates', { fill: C.muted }) +
    // the first home
    pic('arch', x, photoY, w, photoH, { r: 14, fx: 0.52 }) +
    dot(x + w - 22, photoY + 22, 15, C.white) +
    heart(x + w - 22, photoY + 22.5, 16, C.terracotta) +
    t(x, photoY + photoH + 24, 14, 'Clay house, Alentejo', { weight: 500 }) +
    star(x + w - 30, photoY + photoH + 19, 6, C.ink) +
    t(x + w, photoY + photoH + 24, 13, '4.9', { anchor: 'end', weight: 500 }) +
    t(x, photoY + photoH + 43, 13, '2 h 40 from Lisbon', { fill: C.muted }) +
    rich(x, photoY + photoH + 64, 14, [['€84', { weight: 500 }], [' night', { fill: C.muted }]]) +
    // the next home
    pic('desert', x, sy + 364, 52, 52, { r: 10, fy: 0.3 }) +
    t(x + 64, sy + 385, 13, 'Dune house, Merzouga', { weight: 500 }) +
    rich(x + 64, sy + 405, 13, [['€120', { weight: 500 }], [' night', { fill: C.muted }]]) +
    // the tab bar
    line(sx, tabY, sx + sw, tabY, C.rule) +
    magnifier(tab(0) + 8, tabY + 22, C.ink, 5.5) +
    heart(tab(1) + 8, tabY + 22, 17, 'none', C.muted) +
    ICON.bag(tab(2), tabY + 14, C.muted) +
    ICON.person(tab(3), tabY + 14, C.muted) +
    rect(sx + sw / 2 - 48, sy + sh - 10, 96, 4, C.ink, 2)
  );
};

/** The home itself: the photo under the status bar, three facts, and Book at the bottom. */
const listingScreen: Screen = (sx, sy, sw, sh) => {
  const x = sx + 16;
  const photoH = 258;
  const top = sy + photoH;
  const barY = sy + sh - 90;
  const scrim = uid('scrim');
  const fact = (i: number, icon: (x: number, y: number, c: string) => string, str: string) =>
    icon(x, top + 88 + i * 30, C.soft) + t(x + 26, top + 100 + i * 30, 13, str, { fill: C.soft });
  return (
    pic('arch', sx, sy, sw, photoH, { fx: 0.5 }) +
    // a soft shade under the status bar, so the white icons hold on the pale wall
    `<defs><linearGradient id="${scrim}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${C.ink}" stop-opacity="0.32"/><stop offset="1" stop-color="${C.ink}" stop-opacity="0"/></linearGradient></defs>` +
    `<rect x="${sx}" y="${sy}" width="${sw}" height="96" fill="url(#${scrim})"/>` +
    statusBar(sx, sy, sw, C.white) +
    dot(sx + 32, sy + 64, 16, C.white) +
    stroke(`M ${sx + 34.5} ${sy + 58} L ${sx + 28.5} ${sy + 64} L ${sx + 34.5} ${sy + 70}`, C.ink, 2) +
    dot(sx + sw - 32, sy + 64, 16, C.white) +
    heart(sx + sw - 32, sy + 64.5, 16, C.terracotta) +
    t(x, top + 34, 18, 'Clay house, Alentejo', { weight: 500 }) +
    star(x + 6, top + 53, 6, C.ink) +
    rich(x + 17, top + 58, 13, [['4.9', { weight: 500 }], [' · 2 h 40 from Lisbon', { fill: C.muted }]]) +
    line(x, top + 74, sx + sw - 16, top + 74, C.rule) +
    fact(0, ICON.calendar, 'Free 14 to 18 Oct') +
    fact(1, ICON.house, 'Owners away until June') +
    fact(2, ICON.bed, 'Sleeps 4') +
    line(sx, barY, sx + sw, barY, C.rule) +
    rich(x, barY + 32, 15, [['€84', { weight: 500 }], [' night', { fill: C.muted }]]) +
    t(x, barY + 52, 13, '14 to 18 Oct', { fill: C.muted }) +
    rect(sx + sw - 16 - 96, barY + 16, 96, 42, C.terracotta, 21) +
    t(sx + sw - 16 - 48, barY + 42, 15, 'Book', { anchor: 'middle', weight: 500, fill: C.white }) +
    rect(sx + sw / 2 - 48, sy + sh - 10, 96, 4, C.ink, 2)
  );
};

/* ── the page furniture ───────────────────────────────────────────────────────────────────── */

/** The quiet running line: the deck at the left, the page at the right. */
const running = (page: number) =>
  t(L, 50, 13, 'Offseason', { fill: C.muted }) + t(R, 50, 13, String(page).padStart(2, '0'), { fill: C.muted, anchor: 'end' });

/** The headline, always in the same place: serif, regular, top left. */
const headline = (rows: string[]) => lines(L, HEAD_Y, 64, HEAD_LEAD, rows, { font: SERIF, fill: C.ink, tracking: '-0.01em' });

/** The one supporting line a page may have, under the headline. */
const support = (y: number, str: string) => t(L, y, 22, str, { fill: C.soft, tracking: '-0.01em' });

/** A content page: paper, the running line, the headline, and the picture that makes the point. */
const page = (no: number, head: string[], visual: string) =>
  wrap(rect(0, 0, 1280, 720, C.paper) + running(no) + headline(head) + visual);

/* ── 1. Cover ─────────────────────────────────────────────────────────────────────────────── */

/** The name, the line, the round, and the photograph that says the rest. The "O" sits 4px left so its
 *  curve, not its side bearing, lines up with the photo's edge. */
const cover = wrap(
  rect(0, 0, 1280, 720, C.paper) +
    t(L, 50, 13, 'Seed round · 2026', { fill: C.muted }) +
    lines(L - 4, 206, 112, 112, ['Offseason'], { font: SERIF, fill: C.ink, tracking: '-0.02em' }) +
    t(R, 206, 24, 'Stay in the homes people only use in summer.', { anchor: 'end', tracking: '-0.01em' }) +
    pic('cliff', L, 256, R - L, 400, { fy: 0.56 }),
);

/* ── 2. The problem: a year of nights ─────────────────────────────────────────────────────── */

const DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
/** The nights the owners are there: the end of June, July, most of August. 65 in all. */
const used = (m: number, d: number) => (m === 5 && d >= 25) || m === 6 || (m === 7 && d <= 28);

/** 365 nights as twelve month columns, three nights a row, the first of the month at the top left. */
function yearOfNights(top: number): string {
  const pitch = 25;
  const r = 8;
  const cols = 3;
  const groupW = (cols - 1) * pitch + r * 2;
  const step = (R - L - groupW) / 11;
  let out = '';
  DAYS.forEach((days, m) => {
    const gx = L + m * step;
    for (let d = 1; d <= days; d++) {
      const cx = gx + r + ((d - 1) % cols) * pitch;
      const cy = top + r + Math.floor((d - 1) / cols) * pitch;
      out += used(m, d) ? dot(cx, cy, r, C.terracotta) : ring(cx, cy, r - 0.8, C.empty, 1.6);
    }
    out += t(gx + groupW / 2, top + 10 * pitch + r * 2 + 38, 14, 'JFMAMJJASOND'[m], { anchor: 'middle', fill: C.muted });
  });
  return out;
}

const problem = page(
  2,
  ['A second home sits empty', '300 nights a year.'],
  yearOfNights(BELOW_TOP) +
    // the key, set on the headline's last baseline and ending at the margin
    dot(1024, 213, 6, C.terracotta) +
    t(1038, 218, 15, '65 used', { fill: C.soft }) +
    ring(1125, 213, 5.2, C.empty, 1.6) +
    t(1139, 218, 15, '300 empty', { fill: C.soft }),
);

/* ── 3. The product: two taps ─────────────────────────────────────────────────────────────── */

const product = page(
  3,
  ['Book a quiet week', 'in two taps.'],
  phone(660, VISUAL_TOP, 262, 552, resultsScreen) + phone(954, VISUAL_TOP, 262, 552, listingScreen),
);

/* ── 4. How it is going: nights per month ─────────────────────────────────────────────────── */

const NIGHTS = [40, 90, 150, 210, 260, 330, 420, 590, 810];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];

function nightsChart(): string {
  const x0 = 112;
  const x1 = 1168;
  const base = 612;
  const top = BELOW_TOP;
  const max = 900;
  const X = (i: number) => x0 + (i * (x1 - x0)) / (NIGHTS.length - 1);
  const Y = (v: number) => base - (v / max) * (base - top);
  const grid = [0, 200, 400, 600, 800]
    .map((v) => line(L, Y(v), R, Y(v), v === 0 ? C.empty : C.hair, 1) + t(L, Y(v) - 9, 14, v.toLocaleString('en-US'), { fill: C.muted }))
    .join('');
  const pts = NIGHTS.map((v, i) => `${n(X(i))},${n(Y(v))}`);
  const fill = uid('area');
  const last = NIGHTS.length - 1;
  return (
    `<defs><linearGradient id="${fill}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${C.sage}" stop-opacity="0.8"/><stop offset="1" stop-color="${C.sage}" stop-opacity="0.2"/></linearGradient></defs>` +
    grid +
    `<path d="M ${n(X(0))} ${base} L ${pts.join(' L ')} L ${n(X(last))} ${base} Z" fill="url(#${fill})"/>` +
    `<polyline points="${pts.join(' ')}" fill="none" stroke="${C.forest}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>` +
    NIGHTS.slice(0, last)
      .map((v, i) => `<circle cx="${n(X(i))}" cy="${n(Y(v))}" r="3.5" fill="${C.paper}" stroke="${C.forest}" stroke-width="2"/>`)
      .join('') +
    dot(X(last), Y(NIGHTS[last]), 10, C.paper) +
    dot(X(last), Y(NIGHTS[last]), 6.5, C.terracotta) +
    rich(X(last) - 20, Y(NIGHTS[last]) - 20, 17, [['810', { weight: 500 }], [' in September', { fill: C.soft }]], { anchor: 'end' }) +
    MONTHS.map((mo, i) => t(X(i), base + 32, 14, mo, { anchor: 'middle', fill: C.muted })).join('')
  );
}

const traction = page(
  4,
  ['2,900 nights booked', 'in nine months.'],
  t(R, 218, 14, 'Nights booked per month, 2026', { anchor: 'end', fill: C.muted }) + nightsChart(),
);

/* ── 5. The market: three blocks ──────────────────────────────────────────────────────────── */

function marketBlocks(): string {
  const top = VISUAL_TOP;
  const bottom = 556;
  const h = bottom - top;
  const w = 176;
  const gap = 28;
  const x0 = R - (w * 3 + gap * 2);
  const blocks = [
    { value: '12M', label: ['Second homes', 'in Europe'], share: 1, fill: C.sage },
    { value: '6M', label: ['Within three hours', 'of a city'], share: 0.5, fill: C.terracotta },
    { value: '140', label: ['On Offseason', 'today'], share: 0, fill: C.forest },
  ];
  return blocks
    .map((b, i) => {
      const x = x0 + i * (w + gap);
      const fh = Math.max(6, h * b.share);
      const vy = fh > 90 ? bottom - fh + 58 : bottom - fh - 20;
      return (
        rect(x, top, w, h, C.sand) +
        rect(x, bottom - fh, w, fh, b.fill) +
        lines(x + 20, vy, 48, 48, [b.value], { font: SERIF, fill: C.ink, tracking: '-0.01em' }) +
        line(x, bottom + 24, x + w, bottom + 24, C.empty) +
        lines(x, bottom + 52, 15, 21, b.label, { fill: C.ink, tracking: '0' })
      );
    })
    .join('');
}

const market = page(5, ['6 million homes', 'within three hours', 'of a city.'], marketBlocks());

/* ── 6. How we make money: the receipt ────────────────────────────────────────────────────── */

/** One booking as the receipt shows it: what the guest paid, then where each euro goes. */
function receipt(x: number, y: number, w: number): string {
  const p = 40;
  const l = x + p;
  const r = x + w - p;
  const bw = r - l;
  const cut = Math.round(bw * 0.85);
  const row = (by: number, label: string, amount: string, key?: string, strong = false) =>
    (key ? rect(l, by - 12, 11, 11, key, 2) : '') +
    t(key ? l + 24 : l, by, 18, label, { fill: strong ? C.ink : C.soft, weight: strong ? 500 : 400 }) +
    t(r, by, 18, amount, { anchor: 'end', weight: 500 });
  return (
    rect(x, y, w, 530, C.white, 20) +
    pic('arch', l, y + p, 72, 72, { r: 14, fx: 0.52 }) +
    t(l + 92, y + p + 32, 19, 'Clay house, Alentejo', { weight: 500 }) +
    t(l + 92, y + p + 58, 16, '4 nights · 14 to 18 Oct', { fill: C.muted }) +
    line(l, y + 148, r, y + 148, C.rule) +
    row(y + 204, '€84 × 4 nights', '€336') +
    line(l, y + 240, r, y + 240, C.rule, 1.5, '2 5') +
    row(y + 292, 'Owner earns', '€285.60', C.sage) +
    rect(x, y + 322, w, 60, C.highlight) +
    row(y + 358, 'Offseason keeps', '€50.40', C.terracotta, true) +
    rect(l, y + 432, cut - 2, 14, C.sage, 7) +
    rect(l + cut + 2, y + 432, bw - cut - 2, 14, C.terracotta, 7) +
    t(l, y + 482, 15, '85% to the owner', { fill: C.muted }) +
    t(r, y + 482, 15, '15% to Offseason', { anchor: 'end', fill: C.muted })
  );
}

const model = page(
  6,
  ['We keep 15%', 'of every night.'],
  support(HEAD_Y + HEAD_LEAD + SUPPORT_GAP, 'Listing is free. We only earn when a night is booked.') + receipt(R - 468, VISUAL_TOP, 468),
);

/* ── 7. Why owners stay: three cards ──────────────────────────────────────────────────────── */

interface Mark {
  /** Draws one mark whose box starts at `mx` and sits on the baseline. */
  draw: (mx: number, base: number, fill: string) => string;
  w: number;
  h: number;
  pitch: number;
}

/** `count` marks in a row, filled up to `value`: the mark that holds the fraction is filled only that far. */
function meterRow(x: number, base: number, count: number, value: number, mark: Mark, fill: string): string {
  const id = uid('meter');
  const edge = Math.floor(value) * mark.pitch + (value % 1) * mark.w;
  const row = (color: string) => Array.from({ length: count }, (_, i) => mark.draw(x + i * mark.pitch, base, color)).join('');
  return (
    row(C.unfilled) +
    `<clipPath id="${id}"><rect x="${x - 2}" y="${base - mark.h - 2}" width="${n(edge + 2)}" height="${mark.h + 4}"/></clipPath>` +
    `<g clip-path="url(#${id})">${row(fill)}</g>`
  );
}

/** A house: the roof, the walls, and a door cut out of them in the card's white. */
const HOUSE: Mark = {
  w: 26,
  h: 28,
  pitch: 31.5,
  draw: (x, b, fill) =>
    `<path d="M ${x + 1.5} ${b - 14.5} L ${x + 13} ${b - 26.5} L ${x + 24.5} ${b - 14.5} L ${x + 24.5} ${b - 1} L ${x + 1.5} ${b - 1} Z" fill="${fill}" stroke="${fill}" stroke-width="2.5" stroke-linejoin="round"/>` +
    rect(x + 10.5, b - 11, 5, 10, C.white, 1.5),
};

const STAR: Mark = { w: 38, h: 38, pitch: 48, draw: (x, b, fill) => star(x + 19, b - 18, 19, fill) };

/** Homes listed at the end of each month this year, the small bars under 140. */
const LISTED = [18, 26, 38, 52, 66, 84, 102, 121, 140];

function listedBars(x: number, base: number): string {
  const w = 24;
  const gap = 12;
  const max = 52;
  return LISTED.map((v, i) => {
    const h = Math.round((v / LISTED[LISTED.length - 1]) * max);
    return rect(x + i * (w + gap), base - h, w, h, i === LISTED.length - 1 ? C.forest : C.sage, 3);
  }).join('');
}

const CARD_Y = BELOW_TOP;
const CARD_H = 332;

/** A white card: what it counts, the number, and a small picture of the number at the bottom. */
function statCard(x: number, label: string, value: string, meter: string): string {
  return (
    rect(x, CARD_Y, 368, CARD_H, C.white, 16) +
    t(x + 28, CARD_Y + 46, 15, label, { fill: C.soft }) +
    lines(x + 26, CARD_Y + 154, 96, 96, [value], { font: SERIF, fill: C.ink, tracking: '-0.02em' }) +
    line(x + 28, CARD_Y + 200, x + 340, CARD_Y + 200, C.rule) +
    meter
  );
}

const METER_BASE = CARD_Y + CARD_H - 40;

const owners = page(
  7,
  ['9 in 10 owners list again', 'next season.'],
  statCard(64, 'Homes listed', '140', listedBars(92, METER_BASE)) +
    statCard(456, 'Owners who list again', '92%', meterRow(484, METER_BASE, 10, 9.2, HOUSE, C.terracotta)) +
    statCard(848, 'Average rating', '4.8', meterRow(876, METER_BASE, 5, 4.8, STAR, C.forest)),
);

/* ── 8. The ask ───────────────────────────────────────────────────────────────────────────── */

/** Where the round goes, as one bar across the page with each part named under its start. */
function fundsBar(y: number): string {
  const parts = [
    { name: 'Homes', share: 45, fill: C.terracotta },
    { name: 'Product', share: 35, fill: C.sage },
    { name: 'Team', share: 20, fill: C.mist },
  ];
  const w = R - L;
  let x = L;
  return parts
    .map((p, i) => {
      const pw = (w * p.share) / 100 - (i < parts.length - 1 ? 4 : 0);
      const out = rect(x, y, pw, 24, p.fill, 4) + rich(x, y + 56, 16, [[p.name, { weight: 500 }], [` ${p.share}%`, { fill: C.muted }]]);
      x += (w * p.share) / 100;
      return out;
    })
    .join('');
}

/** The kind of home the round adds, each at a width its photograph can hold without going soft. */
const PLACES: [keyof typeof SRC, string, number, Crop][] = [
  ['desert', 'Dune house, Merzouga', 424, { fy: 0.32 }],
  ['interior', 'Concrete loft, Porto', 424, { fx: 0.44, fy: 0.42, zoom: 1.06 }],
  ['stairs', 'Stair house, Tavira', 256, { fy: 0.45 }],
];

function photoStrip(y: number, h: number): string {
  let x = L;
  return PLACES.map(([name, caption, w, crop]) => {
    const out = pic(name, x, y, w, h, { r: 4, ...crop }) + t(x, y + h + 30, 14, caption, { fill: C.muted });
    x += w + 24;
    return out;
  }).join('');
}

const ask = page(
  8,
  ['We are raising €1.5M.'],
  support(HEAD_Y + SUPPORT_GAP, 'Eighteen months to 2,000 homes.') +
    fundsBar(262) +
    photoStrip(372, 232),
);

export const PITCH_SLIDES: DemoSlide[] = [
  { id: 'pitch-1', title: 'Cover', svg: cover },
  { id: 'pitch-2', title: 'The problem', svg: problem },
  { id: 'pitch-3', title: 'The product', svg: product },
  { id: 'pitch-4', title: 'How it is going', svg: traction },
  { id: 'pitch-5', title: 'The market', svg: market },
  { id: 'pitch-6', title: 'How we make money', svg: model },
  { id: 'pitch-7', title: 'Why owners stay', svg: owners },
  { id: 'pitch-8', title: 'The ask', svg: ask },
];
